import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadEmployeeTemplate, importEmployees } from "../api";

/**
 * Модалка импорта сотрудников из Excel.
 */
export default function ImportEmployeesModal({ show, onClose, onImported }) {
  const { t, i18n } = useTranslation();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [committed, setCommitted] = useState(false);

  const localeMap = { ru: "ru-RU", kk: "kk-KZ", en: "en-US" };
  const numLocale = localeMap[i18n.language] || "ru-RU";

  if (!show) return null;

  const reset = () => { setFile(null); setReport(null); setCommitted(false); };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setReport(null); setCommitted(false);
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      setReport({ ok: false, file_errors: [t("import.only_xlsx")], rows: [], summary: { total: 0, valid: 0, invalid: 0, created: 0 } });
      return;
    }
    setBusy(true);
    try {
      const res = await importEmployees(f, "validate");
      setReport(res);
    } catch (e2) {
      setReport({ ok: false, file_errors: [e2.response?.data?.detail || t("import.file_load_error")], rows: [], summary: { total: 0, valid: 0, invalid: 0, created: 0 } });
    } finally { setBusy(false); }
  };

  const onCommit = async () => {
    if (!file || !report?.ok) return;
    setBusy(true);
    try {
      const res = await importEmployees(file, "commit");
      setReport(res);
      if (res.ok) {
        setCommitted(true);
        onImported?.();
      }
    } catch (e2) {
      setReport(r => ({ ...(r || {}), ok: false, message: e2.response?.data?.detail || t("import.import_error") }));
    } finally { setBusy(false); }
  };

  const summary = report?.summary;
  const fileErrors = report?.file_errors || [];
  const rows = report?.rows || [];
  const canCommit = report?.ok && summary && summary.total > 0 && !committed;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}
         role="dialog" aria-modal="true" data-testid="import-modal">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">

          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-file-earmark-spreadsheet me-2 text-success"></i>
              {t("import.title")}
            </h5>
            <button className="btn-close" onClick={onClose} aria-label={t("common.close")}
                    data-testid="import-close-btn"></button>
          </div>

          <div className="modal-body">

            {/* === Help block === */}
            <div className="alert alert-info" data-testid="import-help">
              <div className="d-flex align-items-start gap-2 mb-2">
                <i className="bi bi-info-circle-fill fs-5"></i>
                <div>
                  <strong>{t("import.help_title")}</strong>
                  <ol className="mb-1 ps-3">
                    <li>{t("import.help_step_1")}</li>
                    <li>{t("import.help_step_2")}</li>
                    <li>{t("import.help_step_3")}</li>
                    <li>{t("import.help_step_4")}</li>
                    <li>{t("import.help_step_5")}</li>
                  </ol>
                </div>
              </div>
              <div className="row g-1 small mt-2">
                <div className="col-md-6">
                  <strong>{t("import.required_fields")}</strong> {t("import.required_list")}
                </div>
                <div className="col-md-6">
                  <strong>{t("import.optional_fields")}</strong> {t("import.optional_list")}
                </div>
                <div className="col-12 mt-1 text-muted">
                  <strong>{t("import.role_legend")}</strong> <code>employee</code> · <code>manager</code> · <code>hr</code> · <code>director</code>.&nbsp;
                  <strong>{t("import.week_legend")}</strong> {t("import.week_format")}&nbsp;
                  <strong>{t("import.iin_legend")}</strong> {t("import.iin_format")}&nbsp;
                  <strong>{t("import.birth_legend")}</strong> {t("import.birth_format")}
                </div>
              </div>
            </div>

            {/* === Actions === */}
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              <button className="btn btn-outline-primary" onClick={() => downloadEmployeeTemplate()}
                      data-testid="import-template-btn">
                <i className="bi bi-download me-1"></i>{t("import.download_template")}
              </button>
              <label className="btn btn-success mb-0" htmlFor="import-file-input">
                <i className="bi bi-upload me-1"></i>
                {file ? t("import.choose_another") : t("import.upload_file")}
              </label>
              <input id="import-file-input" type="file" accept=".xlsx"
                     style={{ display: "none" }} onChange={onFileChange}
                     data-testid="import-file-input" />
              {file && (
                <span className="small text-muted">
                  <i className="bi bi-paperclip me-1"></i>{file.name}
                </span>
              )}
              {busy && (
                <span className="small text-muted">
                  <span className="spinner-border spinner-border-sm me-2"></span>{t("import.checking")}
                </span>
              )}
            </div>

            {/* === File-level errors === */}
            {fileErrors.length > 0 && (
              <div className="alert alert-danger" data-testid="import-file-errors">
                <strong><i className="bi bi-exclamation-triangle me-1"></i>{t("import.file_not_supported")}</strong>
                <ul className="mb-0 ps-3">{fileErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}

            {/* === Summary === */}
            {summary && summary.total > 0 && (
              <div className={`alert ${report.ok ? "alert-success" : "alert-warning"}`}
                   data-testid="import-summary">
                <div className="d-flex justify-content-between flex-wrap gap-3">
                  <div>
                    {t("import.summary", { total: summary.total, valid: summary.valid, invalid: summary.invalid })}
                    {committed && summary.created > 0 && (
                      <> · <strong className="text-success">{t("import.created", { count: summary.created })}</strong></>
                    )}
                  </div>
                  {report.message && <em className="small text-muted">{report.message}</em>}
                </div>
              </div>
            )}

            {/* === Row-by-row report === */}
            {rows.length > 0 && (
              <div className="table-responsive" data-testid="import-rows-table">
                <table className="table table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 50 }}>{t("import.row")}</th>
                      <th style={{ width: 60 }}>{t("import.th_status")}</th>
                      <th>E-mail</th>
                      <th>{t("common.fullname")}</th>
                      <th>{t("import.th_role")}</th>
                      <th className="text-end">{t("import.th_salary")}</th>
                      <th>{t("import.remarks")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={r.ok ? "" : "table-danger"}>
                        <td className="text-muted small">{r.row}</td>
                        <td>
                          {r.ok ? (
                            <span className="badge bg-success"><i className="bi bi-check"></i></span>
                          ) : (
                            <span className="badge bg-danger"><i className="bi bi-x"></i></span>
                          )}
                        </td>
                        <td className="small text-break">{r.email || <em className="text-muted">—</em>}</td>
                        <td className="small">{r.name || <em className="text-muted">—</em>}</td>
                        <td className="small">{r.role}</td>
                        <td className="text-end small">{Number(r.salary || 0).toLocaleString(numLocale)}</td>
                        <td className="small">
                          {r.errors?.length > 0 ? (
                            <ul className="mb-0 ps-3">{r.errors.map((e, j) => <li key={j} className="text-danger">{e}</li>)}</ul>
                          ) : <span className="text-success small">{t("import.all_correct")}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </div>

          <div className="modal-footer">
            {!committed && (
              <>
                <button className="btn btn-light" onClick={() => { reset(); onClose(); }}>
                  {t("common.cancel")}
                </button>
                <button className="btn btn-primary" disabled={!canCommit || busy}
                        onClick={onCommit} data-testid="import-commit-btn">
                  <i className="bi bi-cloud-upload me-1"></i>
                  {busy ? t("import.importing") : t("import.commit")}
                </button>
              </>
            )}
            {committed && (
              <button className="btn btn-success" onClick={() => { reset(); onClose(); }}
                      data-testid="import-done-btn">
                {t("import.done")}
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
